import { getDateOnly } from "@ch/helpers/utils";
import { StorageNamesEnum } from "@ch/helpers/utils/enums";
import type { AppState } from "@ch/redux-store/root-reducer";
import localforage from "localforage";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import getKemChatPayload from "../utils/getKemChatPayload";
import postChatAudio from "../utils/postChatAudio";
import type {
  TKemDefaultPayloadResponseData,
  TKemMessagePayload,
} from "../utils/types";

interface SendAudioMessageResult {
  audioUrl: string;
  message?: string;
}

const useKemSpeech = () => {
  const user = useSelector((state: AppState) => state.user?.currentUser);
  const [isLoading, setIsLoading] = useState(false);
  const [responseAudioUrl, setResponseAudioUrl] = useState<string>();
  const [responseMessage, setResponseMessage] = useState<string>();
  const [defaultChatPayload, setDefaultChatPayload] =
    useState<TKemDefaultPayloadResponseData>();

  const userId = user?.id;
  const userFullName = user?.name
    ? user.name.trim()
    : `${user?.firstName?.trim() ?? ""} ${user?.lastName?.trim() ?? ""}`.trim();

  // Data to be sent to BE (matching mobile app structure)
  const defaultChatProps = useMemo(() => {
    const latestBMI = defaultChatPayload?.latest_bmi?.split(" ")[0];

    return {
      ...defaultChatPayload,
      bmi_measurement_date: getDateOnly(
        defaultChatPayload?.bmi_measurement_date,
      ),
      bp_measurement_date: getDateOnly(defaultChatPayload?.bp_measurement_date),
      date_of_birth: getDateOnly(defaultChatPayload?.date_of_birth),
      latest_bmi: latestBMI ? Number(latestBMI) : undefined,
      exercise_duration_last_30_days:
        defaultChatPayload?.exercise_duration_last_30_days?.toString(),
      location: defaultChatPayload?.location?.trim(),
      user_full_name: defaultChatPayload?.user_full_name ?? userFullName,
      user_id: defaultChatPayload?.user_id ?? userId,
      blood_sugar_measurement_date: getDateOnly(
        defaultChatPayload?.blood_sugar_measurement_date,
      ),
      cholesterol_measurement_date: getDateOnly(
        defaultChatPayload?.cholesterol_measurement_date,
      ),
      height_measurement_date: getDateOnly(
        defaultChatPayload?.height_measurement_date,
      ),
      weight_measurement_date: getDateOnly(
        defaultChatPayload?.weight_measurement_date,
      ),
      waist_circumference_measurement_date: getDateOnly(
        defaultChatPayload?.waist_circumference_measurement_date,
      ),
      hbaic_measurement_date: getDateOnly(
        defaultChatPayload?.hbaic_measurement_date,
      ),
      eye_examination_measurement_date: getDateOnly(
        defaultChatPayload?.eye_examination_measurement_date,
      ),
      foot_examination_measurement_date: getDateOnly(
        defaultChatPayload?.foot_examination_measurement_date,
      ),
      health_condition_measurement_date: getDateOnly(
        defaultChatPayload?.health_condition_measurement_date,
      ),
      procedure_surgeries_measurement_date: getDateOnly(
        defaultChatPayload?.procedure_surgeries_measurement_date,
      ),
      prescription_measurement_date: getDateOnly(
        defaultChatPayload?.prescription_measurement_date,
      ),
    };
  }, [defaultChatPayload, userFullName, userId]);

  const handleGetChatPayload = useCallback(async () => {
    const response = await getKemChatPayload();
    if (response) {
      setDefaultChatPayload(response);
    }
  }, []);

  const sendAudioMessage = useCallback(
    async (audioBlob: Blob): Promise<SendAudioMessageResult | undefined> => {
      const refreshToken = await localforage.getItem<string>(
        StorageNamesEnum.REFRESH_TOKEN,
      );
      const messageProps: TKemMessagePayload = {
        message: "",
        ...defaultChatProps,
        sender_enum: "user",
        created_at: new Date().toISOString(),
        modified_at: new Date().toISOString(),
        is_output_audio: true,
        channel: "web",
      } as TKemMessagePayload;

      const response = await postChatAudio({
        messageProps,
        audioFile: audioBlob,
        audioFileName: "recording.webm",
        onResponseComplete: (data) => {
          setResponseMessage(data.message);
          setResponseAudioUrl(data.audioUrl);
        },
        handleLoading: setIsLoading,
        refreshToken,
      });

      return {
        audioUrl: response?.message ?? "",
        message: response?.message ?? "",
      };
    },
    [defaultChatProps],
  );

  const resetResponse = useCallback(() => {
    setResponseMessage(undefined);
    setResponseAudioUrl(undefined);
  }, []);

  // fetch chat payload on mount
  useEffect(() => {
    const fetchChatPayload = async () => {
      setIsLoading(true);
      await handleGetChatPayload();
      setIsLoading(false);
    };

    void fetchChatPayload();
  }, [handleGetChatPayload]);

  return {
    isLoading,
    sendAudioMessage,
    responseAudioUrl,
    responseMessage,
    resetResponse,
  };
};

export default useKemSpeech;
